import type { TokenResource } from "@sarva/api-client";
import { api, signIn as storeCredentials } from "@/shared/api";
import { rememberTenantId } from "./tenant";

/**
 * `POST /api/v1/auth/launch` — a partner product opened Sarva from a button (the backend's
 * D148).
 *
 * The partner's registered issuer signed a short assertion naming an existing user; the API
 * verifies it and mints an ordinary session, exactly as sign-in does, marked as launched. What
 * comes back is the same `TokenResource` sign-in returns, so it is stored the same way: the
 * token in memory and nowhere else (doc 23 §11a), and the tenant remembered so a reload can
 * present the session cookie for it.
 *
 * (H) No `Idempotency-Key`. The API deliberately takes none on this route: the assertion's own
 * `jti` is its single-use key, and a replayed response would be a second copy of a credential.
 * A retry of a failed exchange therefore cannot succeed — the first attempt spent the
 * assertion — which is why the screen offers "open Sarva again from your clinic system" rather
 * than a retry button.
 */
export async function exchangeLaunchAssertion(
  assertion: string,
): Promise<TokenResource> {
  const envelope = await api.launchSession({ body: { assertion } });
  const token = envelope.data;
  rememberTenantId(token.tenant_id);
  storeCredentials({
    accessToken: token.access_token,
    expiresAt: token.expires_at,
    actorId: token.actor_id,
    tenantId: token.tenant_id,
    sessionId: token.session_id,
    // Not known: the assertion named the user to the API, not to this tab, and `/me` carries
    // no email. The account menu says "Signed in", as it does after a reload.
    email: null,
  });
  return token;
}

/**
 * Where to go after a launch: a same-origin relative path, or the dashboard.
 *
 * (H) An open redirect is the classic way a sign-in link becomes a phishing link, so this
 * admits only a path that begins with exactly one `/`. Refused: `//host` and `/\host` (both
 * read by browsers as another origin), anything with a scheme, any backslash, and control
 * characters — browsers strip a tab or newline from a URL, so `/\t/evil.com` becomes
 * `//evil.com` after the check has passed.
 */
export function safeReturnTo(candidate: string | null): string {
  const fallback = "/";
  if (candidate === null) return fallback;
  if (!candidate.startsWith("/")) return fallback;
  if (candidate.startsWith("//") || candidate.startsWith("/\\")) return fallback;
  // eslint-disable-next-line no-control-regex -- refusing control characters is the point
  if (/[\u0000-\u001f\u007f\\]/.test(candidate)) return fallback;
  return candidate;
}

/**
 * Read the launch parameters out of a URL fragment.
 *
 * The assertion travels in the **fragment** (`/launch#assertion=…`) because a fragment is
 * never sent to a server: it cannot land in an access log, a proxy log or a `Referer`.
 */
export function readLaunchFragment(hash: string): {
  assertion: string;
  returnTo: string;
} {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  return {
    assertion: params.get("assertion") ?? "",
    returnTo: safeReturnTo(params.get("return_to")),
  };
}
